import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import pt from "../locales/pt";
import en from "../locales/en";

const translations = { pt, en };

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const getInitialLanguage = () => {
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        if (parsed?.idioma && (parsed.idioma === "pt" || parsed.idioma === "en")) {
          return parsed.idioma;
        }
      }
      const localLang = localStorage.getItem("preferredLanguage");
      if (localLang && (localLang === "pt" || localLang === "en")) {
        return localLang;
      }
    } catch (e) {
      console.warn("Error reading initial language:", e);
    }
    return "pt";
  };

  const [language, setLanguageState] = useState(getInitialLanguage);

  const setLanguage = useCallback(async (newLang) => {
    if (newLang !== "pt" && newLang !== "en") return;
    setLanguageState(newLang);
    localStorage.setItem("preferredLanguage", newLang);

    // Se houver usuário logado, atualiza o storage e persiste no banco
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        userObj.idioma = newLang;
        localStorage.setItem("user", JSON.stringify(userObj));

        if (userObj.id) {
          const token = localStorage.getItem("tokenAcesso") || localStorage.getItem("token");
          const headers = token ? { Authorization: `Bearer ${token.replace(/^["']|["']$/g, "").trim()}` } : {};
          
          await axios.patch(
            `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/atletas/${userObj.id}/idioma`,
            { idioma: newLang },
            { headers }
          );
        }
      }
    } catch (error) {
      console.warn("Erro ao salvar idioma do usuário no backend:", error);
    }
  }, []);

  // Função de tradução com interpolação e fallback
  const t = useCallback(
    (keyPath, variables = {}) => {
      const keys = keyPath.split(".");
      let result = keys.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : null), translations[language]);

      // Fallback para português caso falte a chave
      if (result === null || result === undefined) {
        result = keys.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : null), translations.pt);
      }

      if (typeof result !== "string") {
        return result || "";
      }

      // Interpolação de variáveis: {count}, {name}, etc.
      let interpolated = result;
      for (const [varName, varValue] of Object.entries(variables)) {
        interpolated = interpolated.replace(new RegExp(`\\{${varName}\\}`, "g"), String(varValue));
      }

      return interpolated;
    },
    [language]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
